import { useMemo } from 'react';
import { YAxisProps } from 'recharts';
import {
  EventDTO,
  EventType,
  IndicatorDTOHack,
  IndicatorFailureDTO,
  IndicatorType,
  SloDTO,
  SloType,
} from '@atlassiansox/phobos-client-ts';

import {
  DataPoint,
  DataPointWithEvents,
  joinEventsToPerformance,
  PerformanceWithEvents,
  SLO,
} from '@/ui';
import { determineSloStatus, getErrorBudget } from '@/components/composed/slo-card/utils';
import { evaluateWarnings, SLOGraphWarning } from '@/controllers/slo-graph-controller/warnings';
import { SLOStatus } from '@/components/composed/slo-card/types';
import { assertUnreachableNever } from '@/utils/types';
import { isHighCardinality } from '@/views/slo-view/utils';

import {
  checkDateIsWithinCompliancePeriod,
  checkTimestampHasSameDayAsDate,
  generateApiGraphErrorConfig,
  getFollowingDay,
  getStartOfDayUTC,
} from './utils';
import { SLOGraphErrorActions, SLOGraphErrorCauses } from './enums';

const TimeoutOrRateLimitErrorShared = {
  ACTIONS: [SLOGraphErrorActions.SeeSlack, SLOGraphErrorActions.RerunCollection],
  TITLE: 'Data collection failed',
};

const WarningsExistError = {
  actions: [SLOGraphErrorActions.SeeErrorLog, SLOGraphErrorActions.SeeSlack],
  // log?: API.SLOIndicator['failures'];
  error:
    'One or more SLI quality warnings were found during analysis. ' +
    'See the error log for more details',
  title: 'SLI Warnings Found',
};

export interface SLOGraphError {
  actions: Array<SLOGraphErrorActions>;
  log?: IndicatorFailureDTO[];
  error: string;
  title: string;
}

export type SLOGraph = {
  error?: SLOGraphError;
  warnings: SLOGraphWarning[];
  status: SLOStatus;
  dataPoints: DataPointWithEvents[];
  errorBudgetDataPoints: DataPointWithEvents[];
  errorBudgetDomain: YAxisProps['domain'];
  publicId: string;
  selectionCallback: any;
  isResponsive?: boolean;
  performance?: PerformanceWithEvents;
};

export type MiniSLOGraph = {
  [k in keyof SLOGraph]: SLOGraph[k] extends DataPointWithEvents[] ? DataPoint[] : SLOGraph[k];
};

// NOTE: Remove me if "OR" events are not part of the payload. Pending as we are
// not sure on future use.
const orEvents: EventType[] = [EventType.BurnGradualOr, EventType.BurnSteepOr];
function filterNonOrEvents(events: EventDTO[]): EventDTO[] {
  return events.filter(({ type }) => !orEvents.includes(type));
}

export const instrumentationErrorToAction = (
  error: IndicatorFailureDTO,
  indicator?: IndicatorDTOHack
): SLOGraphErrorActions => {
  if (error.cause.includes(SLOGraphErrorCauses.MultipleTimeSeries)) {
    return SLOGraphErrorActions.MultipleTimeSeries;
  }

  if (error.cause.includes(SLOGraphErrorCauses.FractionalValue)) {
    return SLOGraphErrorActions.FractionalValue;
  }

  if (error.cause.includes(SLOGraphErrorCauses.NegativeValue)) {
    return SLOGraphErrorActions.NegativeValue;
  }

  if (
    error.cause.includes(SLOGraphErrorCauses.TimestampsDidNotAlign) ||
    error.cause.includes(SLOGraphErrorCauses.InconsistentDurations)
  ) {
    if (
      indicator &&
      indicator.goodQuery?.includes('.fill(0)') &&
      indicator.badQuery?.includes('.fill(0)')
    ) {
      return SLOGraphErrorActions.DatapointTimestampsDidNotAlignWithFilledQueries;
    }
    return SLOGraphErrorActions.DatapointTimestampsDidNotAlign;
  }

  // fallback to generic case
  return SLOGraphErrorActions.InvalidQueryOrBadInstrumentation;
};

interface MissingQueryError {
  error: string;
  title: string;
}

function missingQueryForSLOType(type: SloType): MissingQueryError {
  switch (type) {
    case SloType.ReliabilityCloudwatch:
    case SloType.PerformanceCloudwatch:
      return {
        title: 'Missing CloudWatch queries',
        error: 'Please provide CloudWatch queries for this SLO so that Tome can show you data',
      };
    case SloType.Reliability:
    case SloType.Performance:
      return {
        title: 'Missing SignalFlow queries',
        error: 'Please provide SignalFlow queries for this SLO so that Tome can show you data',
      };
    case SloType.UiPerformance:
      return {
        title: 'Missing Performance Portal queries',
        error:
          'Please provide Performance Portal queries for this SLO so that Tome can show you data',
      };
    default:
      return assertUnreachableNever(type);
  }
}

function invalidConfiguration(type: IndicatorType): string {
  switch (type) {
    case IndicatorType.Grouped:
    case IndicatorType.GroupSignalfx:
    case IndicatorType.GroupedDeleted:
    case IndicatorType.Signalfx:
      return 'Invalid SignalFlow query or bad instrumentation';
    case IndicatorType.Ufo:
      return 'Invalid Performance Portal configuration';
    case IndicatorType.Cloudwatch:
      return 'Invalid CloudWatch configuration';
    default:
      return assertUnreachableNever(type);
  }
}

export function makeSLOGraph(
  objective: SLO['objective'],
  indicator: SLO['indicator'],
  performance: SLO['performance'],
  publicId: SLO['publicId'],
  quality: SLO['quality'],
  type: SLO['type']
): SLOGraph {
  const status = determineSloStatus({ performance, objective });
  const hasFailures: boolean = indicator ? !!indicator.failures.length : false;

  const returnData: SLOGraph = {
    warnings: evaluateWarnings(indicator),
    dataPoints: [],
    errorBudgetDataPoints: [],
    errorBudgetDomain: ['dataMin', 1],
    status,
    error: undefined,
    publicId,
    selectionCallback: null,
  };

  // NOTE: States and Errors are documented here:
  // https://hello.atlassian.net/wiki/spaces/SRE/pages/739415008/SLO+Graph+States

  // Error: MissingQueries
  if (!hasFailures && !performance) {
    return {
      ...returnData,
      // NOTE: Since these are "error only" states, blank out the warnings array
      //  so that component doesn't get confused
      warnings: [],
      error: {
        actions: [SLOGraphErrorActions.AddQueries, SLOGraphErrorActions.SeeSlack],
        ...missingQueryForSLOType(type),
      },
    };
  }

  // Error: InvalidQueryOrBadInstrumentation
  if (indicator && !indicator.enabled) {
    // there are a number of causes for this. let's try and return the most relevant action
    const action: SLOGraphErrorActions = hasFailures
      ? instrumentationErrorToAction(indicator.failures[0], indicator)
      : SLOGraphErrorActions.InvalidQueryOrBadInstrumentation;
    const actions: SLOGraphErrorActions[] = [action];

    if (action !== SLOGraphErrorActions.DatapointTimestampsDidNotAlignWithFilledQueries) {
      actions.push(SLOGraphErrorActions.UpdateQueries);
    }

    const message = invalidConfiguration(indicator.type);

    return {
      ...returnData,
      warnings: [],
      error: generateApiGraphErrorConfig(message, actions, indicator.failures),
    };
  }

  // Errors: NoDataForCompliancePeriod & DataCollectionInProgress
  // NOTE: The below performance.result == null will check for both null and undefined, where result can only be number | undefined | null
  if (performance && performance.result == null) {
    const isWithinCompliancePeriod = checkDateIsWithinCompliancePeriod(
      performance.latestDataPointTimestamp
    );

    // Error: NoDataForCompliancePeriod
    if (isWithinCompliancePeriod) {
      const isStaging: boolean = __DEPLOY_ENV__ === 'staging';
      const isPerfPortalType: boolean = type === SloType.UiPerformance;

      let error: string;
      const actions: Array<SLOGraphErrorActions> = [];

      if (isPerfPortalType) {
        error = `Please verify that there is data for your experience key and split on Perf Portal. If this SLO is newly created, it may take up to 24 hours for Tome to see the data`;
        if (isStaging) {
          error = `This could be because you haven't created an SLO that is linked to the same experience key in our production environment for this staging SLO`;
          actions.push(SLOGraphErrorActions.NoDataPerfPortalTypeStaging);
        } else {
          actions.push(SLOGraphErrorActions.NoDataPerfPortalType);
        }
      } else {
        error = `Tome's data collection is up to date but there is no data to show for this SLO's Compliance Period. Please check your instrumentation or that your SignalFlow query is valid`;
        actions.push(SLOGraphErrorActions.NoData);
      }

      return {
        ...returnData,
        warnings: [],
        error: {
          actions,
          title: 'No data for Compliance Period',
          error,
        },
      };
    }

    // Error: DataCollectionInProgress
    return {
      ...returnData,
      warnings: [],
      error: {
        actions: [SLOGraphErrorActions.SeeSlack],
        title: 'Data collection in progress',
        error: 'This can take up to 4 hours, please wait while Tome collects your data',
      },
    };
  }

  // NOTE: The "Error only" state is over, from here on in we're dealing with
  // the "Graph only" and "Graph and Error" states.
  if (performance) {
    // NOTE: "Daily SLI" graph data
    returnData.dataPoints = performance.dataPoints.map(point => {
      const {
        timestamp,
        daily: { result, goodCount, badCount },
        events,
        incidents,
        isLowTraffic,
      } = point;

      return {
        timestamp: new Date(timestamp).getTime(),
        goodCount,
        badCount,
        result: result !== undefined && result !== null ? result * 100 : undefined,
        events: filterNonOrEvents(events),
        incidents,
        isHighCardinality: isHighCardinality(quality),
        isLowTraffic,
      };
    });

    if (performance.dataPoints.length) {
      // NOTE: "Error Budget" graph data
      if (performance.dataPoints[0].rolling) {
        returnData.errorBudgetDataPoints = performance.dataPoints.map(point => {
          const {
            timestamp,
            rolling: { goodCount, badCount, result },
            isLowTraffic,
            events,
            incidents,
          } = point;

          /* NOTE: The Graph library requires a "result" key to plot data, which
          is a number | undefined */
          return {
            timestamp: new Date(timestamp).getTime(),
            goodCount,
            badCount,
            result: getErrorBudget(result, objective),
            events: filterNonOrEvents(events),
            incidents,
            rollup: result,
            isHighCardinality: isHighCardinality(quality),
            isLowTraffic,
          };
        });

        returnData.errorBudgetDomain = [
          Math.min.apply(null, [
            -25,
            ...returnData.errorBudgetDataPoints.map(p => p.result).filter(Boolean),
          ]),
          100,
        ];
      }

      // https://hello.atlassian.net/browse/PSREAU-1462 we're padding out the datapoints with
      // an empty point for the most recent day without complete data.
      if (
        checkDateIsWithinCompliancePeriod(performance.latestDataPointTimestamp) ||
        !performance.latestDataPointTimestamp
      ) {
        const todayUTC = getStartOfDayUTC(new Date());
        const lastFullDay = performance.dataPoints[performance.dataPoints.length - 1].timestamp;
        const earliestNoDataDate = checkTimestampHasSameDayAsDate(lastFullDay, todayUTC)
          ? todayUTC
          : getFollowingDay(lastFullDay);
        returnData.dataPoints = [
          ...returnData.dataPoints,
          {
            timestamp: new Date(earliestNoDataDate).getTime(),
            goodCount: undefined,
            badCount: undefined,
            result: undefined,
            events: [],
            incidents: [],
          },
        ];
        returnData.errorBudgetDataPoints = [
          ...returnData.errorBudgetDataPoints,
          {
            timestamp: new Date(earliestNoDataDate).getTime(),
            goodCount: undefined,
            badCount: undefined,
            result: undefined,
            events: [],
            incidents: [],
          },
        ];
      }
    }
  }

  // Partial data case
  if (
    indicator &&
    indicator.enabled &&
    (indicator.failures.length > 0 || returnData.warnings.length > 0) &&
    performance &&
    performance.result !== undefined &&
    performance.result !== null
  ) {
    if (hasFailures) {
      return {
        ...returnData,
        error: generateApiGraphErrorConfig(
          TimeoutOrRateLimitErrorShared.TITLE,
          TimeoutOrRateLimitErrorShared.ACTIONS,
          indicator.failures
        ),
      };
    }
    if (returnData.warnings.length > 0) {
      return {
        ...returnData,
        error: generateApiGraphErrorConfig(
          WarningsExistError.title,
          WarningsExistError.actions,
          indicator ? indicator.failures : [],
          WarningsExistError.error
        ),
      };
    }
  }
  return returnData;
}

function useSLOGraph(
  objective: SLO['objective'],
  indicator: SLO['indicator'],
  performance: SLO['performance'],
  publicId: SLO['publicId'],
  quality: SLO['quality'],
  type: SLO['type']
): SLOGraph {
  return useMemo(() => makeSLOGraph(objective, indicator, performance, publicId, quality, type), [
    performance,
    objective,
    indicator,
    publicId,
    quality,
    type,
  ]);
}

export function useMiniSLOGraph(
  objective: SloDTO['objective'],
  indicator: SloDTO['indicator'],
  performance: SloDTO['performance'],
  publicId: SloDTO['publicId'],
  quality: SLO['quality'],
  type: SloDTO['type']
  // NOTE: a bit hacky but we intentionally widen this to MiniSLOGraph to
  // indicate that there won't be any events displayed
): MiniSLOGraph {
  // TODO: rather than do this unnecessary computation of
  // joinEventsToPerformance, we should pull out the common parts of useSLOGraph
  // and then have different implementations for the datapoints. This way we
  // also don't need to intentionally widen the return type from SLOGraph to
  // MiniSLOGraph
  return useSLOGraph(
    objective,
    indicator,
    performance && joinEventsToPerformance({}, [], performance),
    publicId,
    quality,
    type
  );
}

export default useSLOGraph;
